import classNames from 'classnames'
import { ForwardedRef, forwardRef, useCallback, useMemo } from 'react'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ClockIcon,
  QueueListIcon,
} from '@heroicons/react/24/outline'

import { H3 } from '@elements/headings'
import { Button } from '@elements/button'
import { DatePicker, DatePickerProps } from '@components/date-picker'
import { ReservationModal } from '@modules/booking/views/reservation-modal'
import { GuestlistModal } from '@modules/booking/views/guestlist-modal'
import { EventHeader } from '@modules/event/views/event-header'

import { addToDate, getDateFromNow, utcToISO, withDateFormat } from '@utils/date-time'
import { useOpenState } from '@hooks/open-state'
import { Venue, VenueDaySettings, VenueEvent } from 'types/model'
import { useBreakpoint } from '@hooks/breakpoints'
import { breakpoints } from '@utils/breakpoints'
import { widgetAttribution } from '@modules/widget/constants'
import { analytics } from '@lib/analytics'
import { TrackEvent } from '@utils/analytics/types'

const inventoryWidgetAttributions = new Set<string>([
  widgetAttribution.inventoryMap,
  widgetAttribution.inventoryList,
  widgetAttribution.widget,
])

export interface InventoryListHeaderProps {
  venue: Venue
  date?: string
  onDateChange?: DatePickerProps['onChange']
  onReservationModalOpen?: () => void
  onGuestlistModalOpen?: () => void
  daySettings?: VenueDaySettings
  event?: VenueEvent
  showEventPoster?: boolean
  shadow?: boolean
  minDate?: number
  maxDate?: number
  attributionId?: string | null
  affiliateId?: string | null
}

export const InventoryListHeader = forwardRef(function InventoryListHeader(
  {
    venue,
    event,
    daySettings,
    date = new Date().toISOString(),
    onDateChange,
    onReservationModalOpen,
    onGuestlistModalOpen,
    showEventPoster = true,
    shadow = false,
    minDate = getDateFromNow().valueOf(),
    maxDate,
    attributionId,
    affiliateId,
  }: InventoryListHeaderProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const isMobile = useBreakpoint(breakpoints.sm, { max: true })
  const reservationModal = useOpenState()
  const guestlistModal = useOpenState()

  const prevDate = useCallback(() => {
    const newDate = addToDate(date, { days: -1 })

    if (newDate && onDateChange) {
      onDateChange(newDate.toISOString())
    }
  }, [date, onDateChange])

  const nextDate = useCallback(() => {
    const newDate = addToDate(date, { days: 1 })

    if (newDate && onDateChange) {
      onDateChange(newDate.toISOString())
    }
  }, [date, onDateChange])

  const { defaultReservationRequestEnabled, defaultGuestlistEnabledExternally } =
    venue?.config ?? {}

  const allowReservation =
    daySettings?.reservationRequestEnabled !== undefined
      ? daySettings.reservationRequestEnabled
      : defaultReservationRequestEnabled
  const allowGuestlist =
    daySettings?.guestlistEnabledExternally !== undefined
      ? daySettings.guestlistEnabledExternally
      : defaultGuestlistEnabledExternally

  const bothListsAllowed = allowGuestlist && allowReservation

  // Date arithmetic to figure out disabled calendar days and disabled pagination
  const todayUTC = useMemo(() => {
    const dateObj = new Date(date)
    return new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), 0, 0, 0).valueOf()
  }, [date])

  const tomorrowUTC = useMemo(() => {
    const dateObj = new Date(date)
    return new Date(
      dateObj.getFullYear(),
      dateObj.getMonth(),
      dateObj.getDate() + 1,
      0,
      0,
      0
    ).valueOf()
  }, [date])

  const reservationFormAttribution =
    attributionId && inventoryWidgetAttributions.has(attributionId)
      ? widgetAttribution.reservationForm
      : attributionId

  const guestlistFormAttribution =
    attributionId && inventoryWidgetAttributions.has(attributionId)
      ? widgetAttribution.widget
      : attributionId

  return (
    <>
      <div
        className={classNames(
          'w-full space-y-3 md:space-y-6 z-[1] px-3 pt-3 transition-shadow shadow-none',
          {
            'shadow-md': shadow,
          }
        )}
        ref={ref}
      >
        {venue && event ? (
          <EventHeader
            venueName={venue.name}
            description={event.description}
            venueAddress={venue?.location.address}
            eventDate={utcToISO(event.dateStart)}
            eventTimezone={event.timezone}
            eventName={event.name}
            eventPoster={showEventPoster ? event.primaryImage?.filename : undefined}
          />
        ) : !!(date && onDateChange) ? (
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center justify-end w-full space-x-2 md:space-x-4">
              <Button.Tertiary sm onClick={prevDate} disabled={todayUTC <= minDate}>
                <ArrowLeftIcon className="w-4 h-4" />
              </Button.Tertiary>
              <DatePicker
                id="inventory-list-date-picker"
                minDate={minDate}
                maxDate={maxDate}
                onChange={onDateChange}
                displayFormat="EEE, M/d/yyyy"
                value={date}
              />
              <Button.Tertiary
                sm
                onClick={nextDate}
                disabled={maxDate ? tomorrowUTC > maxDate : false}
              >
                <ArrowRightIcon className="w-4 h-4" />
              </Button.Tertiary>
            </div>
          </div>
        ) : (
          <H3 className="!text-[24px]">{withDateFormat(date, 'cccc, MMM d, yyyy')}</H3>
        )}
        <div className="flex items-center justify-center w-full pb-4 space-x-2 border-b md:space-x-4 border-standard-light">
          {allowReservation && (
            <Button.Primary
              className="w-full !text-sm sm:!text-md"
              rightIcon={ClockIcon}
              onClick={() => {
                analytics.trackEvent({
                  event: TrackEvent.Click_RequestTable,
                  venue,
                  eventData: {
                    currency: venue.currencyCode,
                  },
                })

                if (onReservationModalOpen) {
                  onReservationModalOpen()
                } else {
                  reservationModal.open()
                }
              }}
              sm
            >
              {bothListsAllowed && isMobile ? 'Table Request' : 'Request a Table'}
            </Button.Primary>
          )}
          {allowGuestlist && (
            <Button.Primary
              sm
              className="w-full !text-sm sm:!text-md"
              rightIcon={QueueListIcon}
              onClick={onGuestlistModalOpen || guestlistModal.open}
            >
              {bothListsAllowed && isMobile ? 'Guestlist' : 'Join the Guestlist'}
            </Button.Primary>
          )}
        </div>
      </div>

      {venue && (
        <>
          {allowReservation && (
            <ReservationModal
              {...reservationModal}
              venue={venue}
              date={date}
              attributionId={reservationFormAttribution}
              affiliateId={affiliateId}
            />
          )}
          {allowGuestlist && (
            <GuestlistModal
              {...guestlistModal}
              venue={venue}
              date={date}
              attributionId={guestlistFormAttribution}
              affiliateId={affiliateId}
            />
          )}
        </>
      )}
    </>
  )
})
